<template>
  <div class="week-strip">
    <div class="week-header">
      <button type="button" class="week-arrow" @click="slide(-1)"><i class="material-icons">chevron_left</i></button>
      <span class="week-range">{{ rangeLabel }}</span>
      <button type="button" class="week-arrow" :disabled="!canGoNext" @click="slide(1)"><i class="material-icons">chevron_right</i></button>
    </div>
    <div
      class="week-viewport"
      dir="ltr"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="reset"
      @click.capture="onClickCapture"
    >
      <div ref="track" class="week-track" :class="{ 'no-transition': dragX !== null || jumping }" :style="{ transform: trackTransform }">
        <div v-for="week in weeks" :key="week.monday" class="week-pane">
          <button
            v-for="day in week.days"
            :key="day.date"
            type="button"
            class="week-day"
            :class="{ selected: day.date === modelValue, today: day.date === today }"
            :disabled="day.date > today"
            :aria-label="day.title"
            :aria-pressed="day.date === modelValue"
            @click="pick(day.date)"
          >
            <span class="week-day-name">{{ day.name }}</span>
            <span class="week-day-number">{{ day.number }}</span>
            <span class="week-day-hours" :class="{ 'absence-only': day.absenceOnly }">{{ day.hoursLabel }}</span>
            <span class="week-day-extra"><span v-if="day.overtimeLabel" class="week-day-overtime">{{ day.overtimeLabel }}</span></span>
            <i v-if="day.hasAbsence" class="week-day-absence"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import { dateStr, getWeekRange } from "../../utils/salaryPeriod.js";
import { monthShort, weekdayName, weekdayShort } from "../../utils/dateNames.js";

const DRAG_START_PX = 8; // below this a press is still a tap
const SWIPE_PX = 50; // shorter drags spring back
const SLIDE_MS = 200; // same as the .week-track transition
const EDGE_RESISTANCE = 0.25; // how far the strip follows a drag towards the future

const asDate = s => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (s, days) => {
  const d = asDate(s);
  d.setDate(d.getDate() + days);
  return dateStr(d);
};

/**
 * WeekStrip — the week bar on Skrá dag: Monday to Sunday with the hours registered per day. Tap a
 * day to select it; swipe (touch) or use the arrows to change week, which keeps the weekday. No
 * future days. The previous and next week are rendered either side of the visible one so their
 * numbers follow the finger, and are fetched with it in one GET api/Timesheet.
 */
export default {
  name: "WeekStrip",
  props: {
    modelValue: { type: String, required: true }, // the selected day, yyyy-mm-dd
    today: { type: String, required: true },
    canLeave: { type: Function, default: () => true }, // asked before the selected day changes, may be async
  },
  emits: ["update:modelValue"],
  data() {
    return {
      totals: {}, // per date: { minutes, overtimeMinutes, absenceHours, hasAbsence }
      press: null, // where the pointer went down
      dragX: null, // strip offset during a drag
      dragged: false, // the click that ends a drag is not a tap
      sliding: 0, // the week being slid to: -1 previous, 1 next
      jumping: false, // re-centring on the new week after a slide, without animating
    };
  },
  computed: {
    monday() { return dateStr(getWeekRange(asDate(this.modelValue)).from); },
    canGoNext() { return addDays(this.monday, 7) <= this.today; },
    weeks() { return [-7, 0, 7].map(offset => this.buildWeek(addDays(this.monday, offset))); },
    rangeLabel() {
      const label = d => `${d.getDate()}. ${monthShort(this.$t, d)}`;
      return `${label(asDate(this.monday))} – ${label(asDate(addDays(this.monday, 6)))}`;
    },
    trackTransform() { return `translateX(calc(${(-100 / 3) * (1 + this.sliding)}% + ${this.dragX ?? 0}px))`; },
  },
  watch: {
    monday() { this.reload(); },
  },
  created() { this.reload(); },
  beforeUnmount() { clearTimeout(this.slideTimer); },
  methods: {
    fmt(hours) {
      return hours.toLocaleString(this.$i18n.locale?.value || this.$i18n.locale || "is-IS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    buildWeek(monday) {
      const days = Array.from({ length: 7 }, (_, i) => {
        const date = addDays(monday, i);
        const d = asDate(date);
        const total = this.totals[date];
        const overtime = (total?.overtimeMinutes || 0) / 60;
        const worked = Math.max(0, (total?.minutes || 0) / 60 - overtime);
        const absenceOnly = !!total?.hasAbsence && worked + overtime === 0;
        const shown = absenceOnly ? total.absenceHours : worked;
        let hoursLabel = ""; // nothing until the week has loaded
        if (total) hoursLabel = shown > 0 ? this.fmt(shown) : "–";
        return {
          date,
          name: weekdayShort(this.$t, d),
          number: d.getDate(),
          title: `${weekdayName(this.$t, d)} ${d.getDate()}. ${monthShort(this.$t, d)}`,
          hoursLabel,
          overtimeLabel: overtime > 0 ? this.fmt(overtime) : "",
          hasAbsence: !!total?.hasAbsence,
          absenceOnly,
        };
      });
      return { monday, days };
    },
    /** Hours of the visible week and both neighbours. */
    async reload() {
      const from = addDays(this.monday, -7);
      const lastDay = addDays(this.monday, 13);
      const to = lastDay > this.today ? this.today : lastDay;
      try {
        const r = await api.get(`/Timesheet?dateFrom=${from}&dateTo=${to}`);
        const loaded = {};
        for (let date = from; date <= to; date = addDays(date, 1)) loaded[date] = { minutes: 0, overtimeMinutes: 0, absenceHours: 0, hasAbsence: false };
        for (const day of r.data?.days || []) {
          const absences = (day.entries || []).filter(e => e.isAbsence);
          loaded[day.date.substring(0, 10)] = {
            minutes: day.dayMinutes,
            overtimeMinutes: day.overtimeMinutes,
            absenceHours: absences.reduce((sum, e) => sum + (Number(e.absenceHours) || 0), 0),
            hasAbsence: absences.length > 0,
          };
        }
        this.totals = { ...this.totals, ...loaded };
      } catch (e) { console.error(e); } // eslint-disable-line no-console
    },
    async pick(date) {
      if (date !== this.modelValue && await this.canLeave()) this.$emit("update:modelValue", date);
    },
    async slide(direction) {
      if (this.sliding || (direction > 0 && !this.canGoNext)) return;
      if (!(await this.canLeave())) return;
      this.sliding = direction;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.slideTimer = setTimeout(() => this.finishSlide(direction), reducedMotion ? 0 : SLIDE_MS);
    },
    /** The neighbour is in view: select the same weekday there and re-centre on it in the same frame. */
    async finishSlide(direction) {
      const date = addDays(this.modelValue, 7 * direction);
      this.jumping = true;
      this.sliding = 0;
      this.$emit("update:modelValue", date > this.today ? this.today : date);
      await this.$nextTick();
      if (!this.$refs.track) return;
      this.$refs.track.getBoundingClientRect(); // applies the jump before transitions come back
      this.jumping = false;
    },
    onPointerDown(e) {
      this.dragged = false;
      if (e.pointerType === "mouse" || this.sliding) return;
      this.press = { x: e.clientX };
    },
    onPointerMove(e) {
      if (!this.press) return;
      const dx = e.clientX - this.press.x;
      if (this.dragX === null) {
        if (Math.abs(dx) < DRAG_START_PX) return;
        // Captured only now: from the press on, a tap on a day would lose its click
        e.currentTarget.setPointerCapture(e.pointerId);
        this.dragged = true;
      }
      this.dragX = dx < 0 && !this.canGoNext ? dx * EDGE_RESISTANCE : dx;
    },
    onPointerUp() {
      const dragX = this.dragX;
      this.reset();
      if (dragX !== null && Math.abs(dragX) >= SWIPE_PX) this.slide(dragX < 0 ? 1 : -1);
    },
    reset() {
      this.press = null;
      this.dragX = null;
    },
    onClickCapture(e) {
      if (!this.dragged) return;
      this.dragged = false;
      e.stopPropagation();
      e.preventDefault();
    },
  },
};
</script>

<style scoped>
.week-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.35rem; }
.week-range { font-size: 0.875rem; font-weight: 700; color: #344767; }
.week-arrow { display: flex; padding: 0.15rem 0.35rem; border: 0; background: none; color: #344767; }
.week-arrow:disabled { opacity: 0.25; }
.week-viewport { overflow: hidden; border-radius: 0.75rem; background: #f6f7fb; touch-action: pan-y; user-select: none; -webkit-user-select: none; }
.week-track { display: flex; width: 300%; transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
.week-track.no-transition { transition: none; }
.week-pane { flex: 0 0 calc(100% / 3); display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 2px; padding: 4px; }
.week-day { position: relative; display: flex; flex-direction: column; align-items: center; min-width: 0; padding: 0.4rem 0 0.3rem; border: 0; border-radius: 0.6rem; background: none; color: #344767; line-height: 1.2; transition: background-color 0.15s, color 0.15s; }
.week-day:disabled { opacity: 0.35; pointer-events: none; } /* a swipe can still start on a future day */
.week-day-name { font-size: 0.68rem; font-weight: 600; color: #67748e; text-transform: capitalize; }
.week-day-number { font-size: 1.05rem; font-weight: 700; }
.week-day-hours { min-height: 1.1em; margin-top: 0.1rem; font-size: 0.72rem; font-weight: 600; color: #67748e; font-variant-numeric: tabular-nums; }
.week-day-hours.absence-only { color: #ea0606; }
.week-day-extra { display: flex; justify-content: center; min-height: 0.95rem; margin-top: 0.1rem; }
.week-day-overtime { padding: 0 0.3rem; border-radius: 1rem; font-size: 0.62rem; font-weight: 700; color: #8a5a00; background: #fff3cd; font-variant-numeric: tabular-nums; }
.week-day-absence { position: absolute; top: 0.4rem; right: 0.4rem; width: 6px; height: 6px; border-radius: 50%; background: #ea0606; }
.week-day.today:not(.selected) { box-shadow: inset 0 0 0 1.5px #344767; }
.week-day.selected { background: #344767; color: #fff; box-shadow: 0 3px 8px rgba(52, 71, 103, 0.35); }
.week-day.selected .week-day-name, .week-day.selected .week-day-hours { color: rgba(255, 255, 255, 0.8); }
.week-day.selected .week-day-hours.absence-only { color: #ffb3b3; }
.week-day.selected .week-day-absence { background: #ff8a8a; }
@media (prefers-reduced-motion: reduce) {
  .week-track, .week-day { transition: none; }
}
</style>
